import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { jwtDecode } from "jwt-decode";
import { BehaviorSubject, Observable, map, tap } from "rxjs";

import { environment } from "./../../../environments/environment";
import { endpoint } from "./../apis/endpoint";

declare const google: any;

export interface JwtPayload {
  exp: number;
  [key: string]: any;
}

export interface GoogleIdentityCache {
  name?: string;
  given_name?: string;
  email?: string;
  picture?: string;
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly googleIdentityKey = "google_identity";
  private authStateSubject = new BehaviorSubject<boolean>(this.hasValidToken());
  public authState$ = this.authStateSubject.asObservable();

  constructor(private http: HttpClient, private router: Router) {}

  loginWithGoogle(googleToken: string): Observable<any> {
    const headers = new HttpHeaders({ "Content-Type": "application/json" });
    const body = JSON.stringify(googleToken);

    return this.http
      .post<any>(
        `${environment.api}${endpoint.LOGIN_GOOGLE_CLIENT}?authType=Externo`,
        body,
        { headers }
      )
      .pipe(
        tap((resp) => {
          if (resp.isSuccess) {
            localStorage.setItem("token", resp.data);
            const existingIdentity = this.getCachedGoogleIdentity();
            const decoded = this.getDecodedToken<any>();
            this.cacheGoogleIdentity({
              name:
                existingIdentity?.name ||
                decoded?.name ||
                decoded?.family_name ||
                "",
              given_name:
                existingIdentity?.given_name ||
                decoded?.given_name ||
                "",
              email:
                existingIdentity?.email ||
                decoded?.email ||
                decoded?.nameid ||
                "",
              picture:
                existingIdentity?.picture ||
                decoded?.picture ||
                decoded?.image ||
                decoded?.profile_picture ||
                "",
            });
            this.authStateSubject.next(this.hasValidToken());
          }
        }),
        map((resp) => {
          if (!resp?.isSuccess || !resp?.data) {
            throw new Error(resp?.message || "No se pudo iniciar sesión con Google.");
          }

          return resp;
        })
      );
  }

  get userToken(): string {
    return localStorage.getItem("token") || "";
  }

  isAuthenticated(): boolean {
    return this.hasValidToken();
  }

  clearSession(): void {
    localStorage.removeItem("token");
    localStorage.removeItem(this.googleIdentityKey);
    this.authStateSubject.next(false);
  }

  getCurrentClientId(): number | null {
    const decoded = this.getDecodedToken();
    if (!decoded) {
      return null;
    }

    try {
      const rawValue =
        decoded["unique_name"] ??
        decoded["nameid"] ??
        decoded["sub"] ??
        decoded["clientId"];
      const parsed = Number(rawValue);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    } catch {
      return null;
    }
  }

  getDecodedToken<T extends JwtPayload = JwtPayload>(): T | null {
    const token = this.userToken;
    if (!token || this.isTokenExpired(token)) {
      return null;
    }

    try {
      return jwtDecode<T>(token);
    } catch {
      return null;
    }
  }

  cacheGoogleIdentity(identity: GoogleIdentityCache): void {
    localStorage.setItem(this.googleIdentityKey, JSON.stringify(identity || {}));
  }

  getCachedGoogleIdentity(): GoogleIdentityCache | null {
    try {
      const raw = localStorage.getItem(this.googleIdentityKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  logout(redirectToProducts: boolean = true): void {
    this.clearSession();
    if (typeof google !== "undefined" && google?.accounts?.id) {
      google.accounts.id.disableAutoSelect();
    }

    if (redirectToProducts) {
      this.router.navigateByUrl("ecommerce/products");
    }
  }

  redirectToGoogleLogin(redirectUrl?: string): void {
    const targetUrl = redirectUrl || this.router.url || "/ecommerce/products";
    this.router.navigate(["/login"], {
      queryParams: { redirect: targetUrl },
    });
  }

  isTokenExpired(token: string): boolean {
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const now = Math.floor(Date.now() / 1000);
      return decoded.exp < now;
    } catch (e) {
      console.error("Token inválido:", e);
      return true;
    }
  }

  private hasValidToken(): boolean {
    const token = localStorage.getItem("token") || "";
    return !!token && !this.isTokenExpired(token);
  }
}
